import * as Api from '@ViewModels';
import { Button } from '@HoudiniUI/Button';
import { Input } from '@HoudiniUI/Input';
import { Label } from '@HoudiniUI/Label';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../../../models';
import {
	useAddSharedMailboxMutation,
	useRemoveSharedMailboxMutation,
	useSharedMailboxesQuery,
	useTestSharedMailboxMutation,
	useUpdateSharedMailboxMutation,
} from '../../../../../../queries/User/useSharedMailboxes';
import { baseStyleSheet } from '../../../../../styles/styles';
import { SharedMailboxRow, VerifyStatus } from './SharedMailboxRow';
import { styleSheet } from './styles';

interface IProps {
	emailProvider?: Api.EmailProviderType;
}

const isMicrosoftProvider = (emailProvider?: Api.EmailProviderType) =>
	emailProvider === 'Office365' || emailProvider === 'Outlook';

// Generous enough for a slow tenant to deliver, short enough that a silently-dropped test doesn't hang the UI.
const VERIFY_TIMEOUT_MS = 90_000;

const _SharedMailboxSettingsSection: React.FC<IProps & IImpersonationContextComponentProps> = ({
	emailProvider,
	impersonationContext,
}) => {
	const isMicrosoft = isMicrosoftProvider(emailProvider);
	const history = useHistory();
	const location = useLocation();

	// The injected context can be a MobX view model; the query key and request composition want a stable
	// plain object.
	const accountId = impersonationContext?.account?.id;
	const userId = impersonationContext?.user?.id;
	const plainImpersonationContext = React.useMemo<Api.IImpersonationContext | undefined>(
		() => (accountId ? { account: { id: accountId }, user: userId ? { id: userId } : undefined } : undefined),
		[accountId, userId]
	);

	const [testingId, setTestingId] = React.useState<string | null>(null);
	const [verifyStatuses, setVerifyStatuses] = React.useState<Record<string, VerifyStatus>>({});
	const [verifyMessages, setVerifyMessages] = React.useState<Record<string, string | null>>({});
	const [saveErrors, setSaveErrors] = React.useState<Record<string, string | null>>({});
	const [addError, setAddError] = React.useState<string | null>(null);
	const [isAdding, setIsAdding] = React.useState(false);
	const [editingId, setEditingId] = React.useState<string | null>(null);
	const [newAddress, setNewAddress] = React.useState('');
	const [newName, setNewName] = React.useState('');
	const [pendingTestIds, setPendingTestIds] = React.useState<string[]>([]);

	const { data, isLoading } = useSharedMailboxesQuery({
		impersonationContext: plainImpersonationContext,
		pollWhileTesting: true,
		meta: {
			toastErrorMessage: 'Failed to load shared mailbox settings',
			errorLogging: { category: 'SharedMailboxSettings', action: 'Query-Error' },
		},
	});

	const setStatus = React.useCallback((id: string, status: VerifyStatus, message: string | null = null) => {
		setVerifyStatuses(prev => ({ ...prev, [id]: status }));
		setVerifyMessages(prev => ({ ...prev, [id]: message }));
	}, []);

	const testMutation = useTestSharedMailboxMutation({
		impersonationContext: plainImpersonationContext,
		onError: error => {
			const id = testingId;
			setTestingId(null);
			if (id) {
				setStatus(id, 'error', error.systemMessage || 'We couldn’t verify the shared mailbox. Try again.');
			}
		},
		onSuccess: ({ id }) => setStatus(id, 'validating'),
	});

	const observedInFlightRef = React.useRef(false);
	const startedVerifiedRef = React.useRef(false);

	const runTest = (id: string) => {
		observedInFlightRef.current = false;
		startedVerifiedRef.current = data?.mailboxes.find(x => x.id === id)?.verified ?? false;
		setTestingId(id);
		setStatus(id, 'validating');
		testMutation.mutate({ id });
	};

	// Only one test may run at a time, so queued mailboxes start as the previous one settles.
	React.useEffect(() => {
		if (testingId || pendingTestIds.length === 0) {
			return;
		}
		const [next, ...rest] = pendingTestIds;
		setPendingTestIds(rest);
		runTest(next);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [testingId, pendingTestIds]);

	// The server clears the in-flight marker on both outcomes, so its disappearance is what settles a test.
	// The cached collection still reads pre-test on the first renders after a click, so only settle once the
	// marker has actually been observed (or `verified` flipped between polls, meaning the test won).
	React.useEffect(() => {
		if (!testingId || !data) {
			return;
		}
		const mailbox = data.mailboxes.find(x => x.id === testingId);
		if (!mailbox) {
			setTestingId(null);
			return;
		}
		if (mailbox.testInFlight) {
			observedInFlightRef.current = true;
			return;
		}
		// The server drops verification when it claims the test lock, so a previously-verified mailbox
		// reading unverified is also proof the test started.
		if (startedVerifiedRef.current && !mailbox.verified) {
			observedInFlightRef.current = true;
		}
		const verifiedFlipped = mailbox.verified && !startedVerifiedRef.current;
		if (!observedInFlightRef.current && !verifiedFlipped) {
			return;
		}
		setTestingId(null);
		if (mailbox.verified) {
			setStatus(testingId, 'success');
		} else {
			const providerLabel = isMicrosoftProvider(emailProvider) ? 'Office 365' : 'Your email provider';
			setStatus(
				testingId,
				'error',
				`${providerLabel} sent the test as you instead of ${mailbox.emailAddress}, which means you don’t have permission to send from that mailbox yet.`
			);
		}
	}, [data, testingId, emailProvider, setStatus]);

	React.useEffect(() => {
		if (!testingId) {
			return;
		}
		const timer = window.setTimeout(() => {
			setTestingId(null);
			setStatus(
				testingId,
				'error',
				'We sent the test but couldn’t confirm it arrived from the shared mailbox. Check that you have permission to send from it, then try again.'
			);
		}, VERIFY_TIMEOUT_MS);
		return () => window.clearTimeout(timer);
	}, [testingId, setStatus]);

	const addMutation = useAddSharedMailboxMutation({
		impersonationContext: plainImpersonationContext,
		onError: error => setAddError(error.systemMessage || 'Failed to add the shared mailbox.'),
		onSuccess: collection => {
			setAddError(null);
			setIsAdding(false);
			setNewAddress('');
			setNewName('');
			toast.success('Shared mailbox saved.');
			// The collection's ordering is not guaranteed, so identify the new mailbox by diffing ids.
			const priorIds = new Set((data?.mailboxes ?? []).map(x => x.id));
			const added = collection.mailboxes.find(x => !priorIds.has(x.id));
			if (added && !collection.reconnectRequired) {
				setPendingTestIds(prev => [...prev, added.id]);
			}
		},
	});

	const [savingIds, setSavingIds] = React.useState<string[]>([]);
	const updateMutation = useUpdateSharedMailboxMutation({
		impersonationContext: plainImpersonationContext,
		onError: (error, { id }) => {
			setSavingIds(prev => prev.filter(x => x !== id));
			setSaveErrors(prev => ({ ...prev, [id]: error.systemMessage || 'Failed to save the shared mailbox.' }));
		},
		onSuccess: (_data, { id }) => {
			setSavingIds(prev => prev.filter(x => x !== id));
			setEditingId(prev => (prev === id ? null : prev));
			toast.success('Shared mailbox saved.');
		},
	});

	const [removingIds, setRemovingIds] = React.useState<string[]>([]);
	const removeMutation = useRemoveSharedMailboxMutation({
		impersonationContext: plainImpersonationContext,
		onError: (error, { id }) => {
			setRemovingIds(prev => prev.filter(x => x !== id));
			toast.error(error.systemMessage || 'Failed to remove the shared mailbox.');
		},
		onSuccess: (_data, { id }) => {
			setRemovingIds(prev => prev.filter(x => x !== id));
			toast.success('Shared mailbox removed.');
		},
	});

	const onRemoveMailbox = (id: string) => {
		setRemovingIds(prev => [...prev, id]);
		removeMutation.mutate({ id });
	};

	// After a reconnect we return with `?validate=sharedMailbox`. The scope grant is all-or-nothing, so every
	// configured mailbox becomes testable at once and they queue behind one another.
	const autoValidatedRef = React.useRef(false);
	React.useEffect(() => {
		if (!data || autoValidatedRef.current) {
			return;
		}
		const params = new URLSearchParams(location.search);
		if (params.get('validate') === 'sharedMailbox' && data.mailboxes.length > 0 && !data.reconnectRequired) {
			autoValidatedRef.current = true;
			// Already-verified mailboxes keep their proof; re-testing them only risks a spurious timeout.
			setPendingTestIds(data.mailboxes.filter(x => !x.verified).map(x => x.id));
			history.replace(location.pathname);
		}
	}, [data, location.search, location.pathname, history]);

	if (isLoading || !data) {
		return null;
	}

	// Hosted-sending accounts have no provider-side delegation, so the feature doesn't apply at all.
	// (`available` is undefined on older API builds, so only an explicit `false` hides the section.)
	if (data.available === false) {
		return null;
	}

	const onSaveMailbox = (id: string, values: { emailAddress: string; name?: string }) => {
		setSaveErrors(prev => ({ ...prev, [id]: null }));
		setSavingIds(prev => [...prev, id]);
		updateMutation.mutate({ id, ...values });
	};

	const onAdd = () => {
		const trimmed = newAddress.trim();
		if (!trimmed) {
			setAddError('Enter the shared mailbox email address.');
			return;
		}
		setAddError(null);
		addMutation.mutate({ emailAddress: trimmed, name: newName.trim() || undefined });
	};

	const reconnectHref = `${window.location.origin}/#/user/connect-email?reconnect=true&returnUrl=${encodeURIComponent(
		'/settings/personal/profile?validate=sharedMailbox'
	)}`;

	const testInFlight = !!testingId || !!data.testInFlightId;
	const atCap = data.mailboxes.length >= data.maxMailboxes;

	return (
		<div className={css(baseStyleSheet.mb2, styleSheet.container)}>
			<div className={css(styleSheet.sectionHeader)}>Send from a shared mailbox</div>
			<div className={css(styleSheet.disclaimer)}>
				Note: You can connect a shared mailbox you have permission to send from. Distribution lists / distribution
				groups can’t be connected because they don’t have a mailbox to send from. Not sure? If you can set the address
				as your “From” in Outlook/Gmail, it’ll work here too.
			</div>

			{isMicrosoft && data.reconnectRequired ? (
				<div className={css(styleSheet.reconnectBanner)}>
					<span className={css(styleSheet.reconnectText)}>
						Reconnect Office 365 to grant permission to send from your shared mailboxes, then we’ll verify they can
						send.
					</span>
					<Button variant='secondary' size='sm' asChild>
						<a href={reconnectHref}>Reconnect Office 365</a>
					</Button>
				</div>
			) : null}

			<div className={css(styleSheet.fields)}>
				{data.mailboxes.map(mailbox => (
					<SharedMailboxRow
						key={mailbox.id}
						mailbox={mailbox}
						isMicrosoft={isMicrosoft}
						isEditing={editingId === mailbox.id}
						verifyStatus={verifyStatuses[mailbox.id] ?? 'idle'}
						verifyMessage={verifyMessages[mailbox.id] ?? null}
						saveError={saveErrors[mailbox.id] ?? null}
						isSaving={savingIds.includes(mailbox.id)}
						isRemoving={removingIds.includes(mailbox.id)}
						testDisabled={testInFlight || (isMicrosoft && data.reconnectRequired)}
						onStartEdit={() => setEditingId(mailbox.id)}
						onCancelEdit={() => {
							setEditingId(null);
							setSaveErrors(prev => ({ ...prev, [mailbox.id]: null }));
						}}
						onSave={values => onSaveMailbox(mailbox.id, values)}
						onRemove={() => onRemoveMailbox(mailbox.id)}
						onTest={() => runTest(mailbox.id)}
					/>
				))}

				{isAdding ? (
					<div className={css(styleSheet.mailboxRow)}>
						<div className={css(styleSheet.field)}>
							<Label htmlFor='newSharedMailboxAddress' styles={[styleSheet.fieldLabel]}>
								Address
							</Label>
							<Input
								id='newSharedMailboxAddress'
								type='email'
								placeholder='team@company.com'
								value={newAddress}
								onChange={e => setNewAddress(e.target.value)}
								styles={[styleSheet.inputControl]}
							/>
						</div>
						<div className={css(styleSheet.field)}>
							<Label htmlFor='newSharedMailboxName' styles={[styleSheet.fieldLabel]}>
								Display name (optional)
							</Label>
							<Input
								id='newSharedMailboxName'
								type='text'
								placeholder='Team Inbox'
								value={newName}
								onChange={e => setNewName(e.target.value)}
								styles={[styleSheet.inputControl]}
							/>
						</div>
						{!isMicrosoft ? (
							<div className={css(styleSheet.gmailHint)}>
								The address must be configured as a “send-as” alias in your Google account.
							</div>
						) : null}
						{addError ? <div className={css(styleSheet.validationError)}>{addError}</div> : null}
						<div className={css(styleSheet.actions)}>
							<Button
								variant='primary'
								size='sm'
								isLoading={addMutation.isPending}
								onClick={onAdd}
								styles={[styleSheet.actionButton]}
							>
								Save shared mailbox
							</Button>
							<Button
								variant='secondary'
								size='sm'
								onClick={() => {
									setIsAdding(false);
									setAddError(null);
								}}
								styles={[styleSheet.actionButton]}
							>
								Cancel
							</Button>
						</div>
					</div>
				) : (
					<div className={css(styleSheet.addRow)}>
						<Button
							variant='secondary'
							size='sm'
							disabled={atCap || testInFlight}
							onClick={() => setIsAdding(true)}
							styles={[styleSheet.actionButton]}
						>
							{data.mailboxes.length === 0 ? 'Add a shared mailbox' : 'Add another shared mailbox'}
						</Button>
						{atCap ? (
							<span className={css(styleSheet.addHint)}>
								You’ve reached the limit of {data.maxMailboxes}. Remove one to add another.
							</span>
						) : null}
						{!atCap && testInFlight ? (
							<span className={css(styleSheet.addHint)}>
								You can add another once the mailbox being tested finishes.
							</span>
						) : null}
					</div>
				)}
			</div>
		</div>
	);
};

export const SharedMailboxSettingsSection = inject(ImpersonationContextKey)(
	observer(_SharedMailboxSettingsSection)
) as React.FC<IProps>;
